import { useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery } from "convex/react";
import { Settings2, ShieldCheck, Tag } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { DefaultsSection } from "@/components/settings/defaults-section";
import { SettingsNav } from "@/components/settings/settings-nav";
import type { SettingsNavItem } from "@/components/settings/settings-nav";
import type {
  ManagedTag,
  SettingsSection,
  TagDraft,
  TagMutationResult,
} from "@/components/settings/settings-types";
import {
  cleanInput,
  describeRejectedAction,
  emptyTagDraft,
  formatCount,
  getTagMutationToast,
  validateTagDraft,
} from "@/components/settings/settings-utils";
import { TagsSection } from "@/components/settings/tag-management-section";

function findManagedTag(
  tags: ManagedTag[],
  tagId: Id<"tag"> | null,
): ManagedTag | null {
  if (tagId === null) {
    return null;
  }

  const tag = tags.find((candidate) => candidate._id === tagId);
  if (tag === undefined) {
    return null;
  }

  return tag;
}

/** Settings screen for property tags, maintenance tools, and guarded batch jobs. */
export function SettingsPage() {
  const [activeSection, setActiveSection] = useState<SettingsSection>("tags");
  const [createTagDraft, setCreateTagDraft] = useState<TagDraft>(emptyTagDraft);
  const [editTagDraft, setEditTagDraft] = useState<TagDraft>(emptyTagDraft);
  const [editingTagId, setEditingTagId] = useState<Id<"tag"> | null>(null);
  const [archivingTagId, setArchivingTagId] = useState<Id<"tag"> | null>(null);
  const [createTagError, setCreateTagError] = useState<string | null>(null);
  const [editTagError, setEditTagError] = useState<string | null>(null);
  const [archiveTagError, setArchiveTagError] = useState<string | null>(null);
  const [pendingTagAction, setPendingTagAction] = useState<
    Id<"tag"> | "new" | null
  >(null);

  const tagSummary = useQuery(api.tags.getTagManagementSummary, {});
  const createTag = useMutation(api.tags.createTag);
  const updateTag = useMutation(api.tags.updateTag);
  const archiveTag = useMutation(api.tags.archiveTag);

  const managedTags = tagSummary === undefined ? [] : tagSummary.tags;
  const totalTags = tagSummary === undefined ? 0 : tagSummary.totalTags;
  const totalAssignments = managedTags.reduce(
    (sum, tag) => sum + tag.usageCount,
    0,
  );
  const editingTag = findManagedTag(managedTags, editingTagId);
  const archivingTag = findManagedTag(managedTags, archivingTagId);

  const settingsSections: SettingsNavItem[] = [
    {
      id: "tags",
      label: "Tags",
      description: "Manage custom property labels",
      status:
        tagSummary === undefined
          ? "Checking tags"
          : `${formatCount(totalTags)} active`,
      icon: Tag,
    },
    {
      id: "defaults",
      label: "Defaults",
      description: "Review listing defaults",
      status: "Planned",
      icon: Settings2,
    },
  ];

  const handleTagMutationResult = (
    result: TagMutationResult,
    setError: (message: string | null) => void,
    onSuccess: () => void,
  ) => {
    const message = getTagMutationToast(result);
    if (result.ok === false) {
      setError(message);
      toast.error(message);
      return;
    }

    setError(null);
    onSuccess();
    toast.success(message);
  };

  const handleRejectedTagMutation = (
    error: unknown,
    fallbackMessage: string,
  ) => {
    const message = describeRejectedAction(error);
    console.error(fallbackMessage, error);
    console.error(message);
    setCreateTagError(fallbackMessage);
    toast.error(fallbackMessage);
  };

  const handleCreateTagSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateTagDraft(createTagDraft);
    if (validationError !== null) {
      setCreateTagError(validationError);
      return;
    }

    setPendingTagAction("new");
    const payload = {
      tag_name: cleanInput(createTagDraft.tagName),
      tag_name_idn: cleanInput(createTagDraft.tagNameIdn),
    };

    void createTag(payload)
      .then((result: TagMutationResult) => {
        handleTagMutationResult(result, setCreateTagError, () => {
          setCreateTagDraft(emptyTagDraft);
        });
      })
      .then(undefined, (error: unknown) => {
        handleRejectedTagMutation(
          error,
          "Property tag was not created. Try saving it again.",
        );
      })
      .finally(() => {
        setPendingTagAction(null);
      });
  };

  const handleEditTagSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateTagDraft(editTagDraft);
    if (validationError !== null) {
      setEditTagError(validationError);
      return;
    }

    if (editingTagId === null) {
      setEditTagError("Choose a tag to edit before saving.");
      return;
    }

    setPendingTagAction(editingTagId);
    const payload = {
      tag_name: cleanInput(editTagDraft.tagName),
      tag_name_idn: cleanInput(editTagDraft.tagNameIdn),
    };

    void updateTag({ id: editingTagId, ...payload })
      .then((result: TagMutationResult) => {
        handleTagMutationResult(result, setEditTagError, () => {
          setEditingTagId(null);
          setEditTagDraft(emptyTagDraft);
        });
      })
      .then(undefined, (error: unknown) => {
        const message = describeRejectedAction(error);
        console.error(
          "Property tag was not updated. Try saving it again.",
          error,
        );
        console.error(message);
        setEditTagError("Property tag was not updated. Try saving it again.");
        toast.error("Property tag was not updated. Try saving it again.");
      })
      .finally(() => {
        setPendingTagAction(null);
      });
  };

  const handleStartEditTag = (tag: ManagedTag) => {
    setEditingTagId(tag._id);
    setEditTagDraft({
      tagName: tag.tag_name,
      tagNameIdn: tag.tag_name_idn,
    });
    setEditTagError(null);
    setActiveSection("tags");
  };

  const handleCancelTagEdit = () => {
    setEditingTagId(null);
    setEditTagDraft(emptyTagDraft);
    setEditTagError(null);
  };

  const handleStartArchiveTag = (tag: ManagedTag) => {
    setArchivingTagId(tag._id);
    setArchiveTagError(null);
    setActiveSection("tags");
  };

  const handleCancelArchiveTag = () => {
    setArchivingTagId(null);
    setArchiveTagError(null);
  };

  const handleArchiveTag = (tag: ManagedTag) => {
    setPendingTagAction(tag._id);
    void archiveTag({ id: tag._id })
      .then((result: TagMutationResult) => {
        handleTagMutationResult(result, setArchiveTagError, () => {
          setArchivingTagId(null);
        });
      })
      .then(undefined, (error: unknown) => {
        const message = describeRejectedAction(error);
        console.error("Property tag was not archived. Try again.", error);
        console.error(message);
        setArchiveTagError("Property tag was not archived. Try again.");
        toast.error("Property tag was not archived. Try again.");
      })
      .finally(() => {
        setPendingTagAction(null);
      });
  };

  return (
    <div className="w-full max-w-none overflow-y-auto max-h-[calc(100vh-200px)] min-h-screen">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Settings</h3>
          <p className="text-sm text-muted-foreground">
            Manage property tags and listing defaults.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" />
          Tag changes stay guarded
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-background">
        <div className="grid min-h-[34rem] lg:grid-cols-[13rem_1fr]">
          <SettingsNav
            activeSection={activeSection}
            sections={settingsSections}
            onSelect={setActiveSection}
          />

          <main className="min-w-0 p-3">
            {activeSection === "tags" ? (
              <TagsSection
                tags={managedTags}
                isLoading={tagSummary === undefined}
                totalTags={totalTags}
                totalAssignments={totalAssignments}
                createDraft={createTagDraft}
                editDraft={editTagDraft}
                editingTag={editingTag}
                archivingTag={archivingTag}
                pendingTagAction={pendingTagAction}
                createFormError={createTagError}
                editFormError={editTagError}
                archiveFormError={archiveTagError}
                onCreateDraftChange={setCreateTagDraft}
                onEditDraftChange={setEditTagDraft}
                onCreateSubmit={handleCreateTagSubmit}
                onEditSubmit={handleEditTagSubmit}
                onEdit={handleStartEditTag}
                onCancelEdit={handleCancelTagEdit}
                onRequestArchive={handleStartArchiveTag}
                onCancelArchive={handleCancelArchiveTag}
                onArchive={handleArchiveTag}
              />
            ) : null}

            {activeSection === "defaults" ? (
              <DefaultsSection onOpenTags={() => setActiveSection("tags")} />
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
